"use client";

import type { InputHTMLAttributes, ReactNode } from "react";

import { cn } from "../../utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  error?: boolean;
  warning?: boolean;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  containerClassName?: string;
  inputWrapperClassName?: string;
}

export const Input = ({
  id,
  ref,
  label,
  error,
  warning,
  disabled,
  leftSlot,
  rightSlot,
  className,
  containerClassName,
  inputWrapperClassName,
  ...props
}: InputProps) => {
  const Wrapper = label ? "div" : "label";

  const inputElement = (
    <Wrapper
      {...(label ? {} : { htmlFor: id })}
      className={cn(
        "ui:flex ui:h-11 ui:items-center ui:gap-2 ui:rounded-sm ui:border ui:border-divider ui:px-3 ui:transition-colors",
        {
          "ui:cursor-text": !disabled,
          "ui:cursor-not-allowed": disabled,
          "ui:border-error": error,
          "ui:border-warning": warning,
          "ui:hover:border-primary ui:has-focus:border-primary":
            !error && !warning && !disabled,
        },
        inputWrapperClassName,
      )}
    >
      {leftSlot}

      <input
        id={id}
        ref={ref}
        disabled={disabled}
        {...props}
        className={cn(
          "ui:flex-1 ui:text-textPrimary ui:outline-none ui:placeholder:text-textSecondary",
          {
            "ui:cursor-not-allowed": disabled,
          },
          className,
        )}
      />

      {rightSlot}
    </Wrapper>
  );

  if (!label) return inputElement;

  return (
    <label
      htmlFor={id}
      className={cn(
        "ui:flex ui:cursor-text ui:flex-col ui:gap-1 ui:text-xs ui:text-textPrimary",
        containerClassName,
      )}
    >
      {label}
      {inputElement}
    </label>
  );
};
